import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMoneyBill, FaEye } from 'react-icons/fa';

import { exportCompanyInvoicesToExcelColumns, InsuranceInvoiceColumns } from '../../../constants/BillingConstants';
import { EyeIcon, PrintIcon } from '../../../utils/TableActionIcons';
import CustomPagination from '../../../shared/CustomPagination';
import CustomTable from '../../../shared/CustomTable';
import { useGetInsuranceInvoicesQuery } from '../../../redux/api/modules/billingApi';
import ReturnUnpaidInvoiceForm from './ReturnUnpaidInvoiceForm';
import BillingFilters from './BillingFilters';
import { useTranslation } from 'react-i18next';
import CheckPermission from '../../../hooks/CheckPermission';
import { fetchData } from '../../../helpers/fetchData';
import { IoIosSend } from 'react-icons/io';
import { toast } from 'react-hot-toast';
import usePersistentBillingFilters from '../../../hooks/usePersistentBillingFilters';
import { useSelector } from 'react-redux';
import ViewInvoiceModal from './ViewInvoiceModal';
import { hasBillingSearchFilters, omitBillingDatesForSkipInitialLoad, shouldSkipInitialBillingLoad } from '../../../utils/businessLevelAccess';
const ReusableModal = React.lazy(() => import('../../../shared/ReuseableModal'));
const ConfirmationModal = React.lazy(() => import('../../../shared/ConfirmationModal'));

function InsuranceInvoice() {
  const [payload, setPayload, resetPayload, appliedPayload, applyFilters] = usePersistentBillingFilters('insurance');
  const [searchQuery, setSearchQuery] = useState(payload?.query || '');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = CheckPermission();
  const authUser = useSelector(state => state?.auth?.user);
  const skipInitialBillingLoad = shouldSkipInitialBillingLoad(authUser);

  const queryPayload = React.useMemo(() => {
        const formData = {
            ...appliedPayload,
            filters: { ...appliedPayload?.filters }
        };
        formData.filters.insurance_invoice_number = appliedPayload?.filters?.invoice_number;
        delete formData?.filters?.invoice_number;
        return formData;
    }, [appliedPayload]);

    const {
        data,
        isLoading: queryLoading,
        isFetching: queryFetching,
        refetch
    } = useGetInsuranceInvoicesQuery(queryPayload, {
        skip: skipInitialBillingLoad && !hasBillingSearchFilters(appliedPayload)
    });

    const loading = queryLoading || queryFetching;
  const [openCancelUnpaidInvoiceModal, setOpenCancelUnpaidInvoiceModal] = useState(false);
  const isSendToZatca = useSelector(state => state?.auth?.zatcaSettings?.enable_send_insurance_invoices);
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [openSendToZatcaModal, setOpenSendToZatcaModal] = useState(false);
  const [zatcaLoading, setZatcaLoading] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [viewInvoiceNumber, setViewInvoiceNumber] = useState(null);
  const [viewFileNumber, setViewFileNumber] = useState(null);
  const [viewPatientId, setViewPatientId] = useState(null);
  const [viewDocumentId, setViewDocumentId] = useState(null);
  const actionsCoumn = {
    body: actionsTemplate,
    header: t('Actions'),
    headerCellClass: 'flex items-center justify-center'
  };
  const columnsData = [actionsCoumn, ...InsuranceInvoiceColumns(t, setSelectedInvoices), ,];

  const fetchInitialData = () => {
        refetch();
    };

  

  function actionsTemplate(rowData) {
    return (
      <div className='flex items-center justify-center gap-2'>
        <EyeIcon
          title={t('View Invoice')}
          size='18'
          onClick={() => {
            setViewInvoiceNumber(rowData?.invoice_number || rowData?.insurance_invoice_number);
            setViewFileNumber(rowData?.patient?.file_no || rowData?.file_no || rowData?.patient_file_no);
            setViewPatientId(rowData?.patient?.patient_id || rowData?.patient_id || rowData?.patientId || rowData?.beneficiary_id);
            setViewDocumentId(rowData?.patient?.document_id || rowData?.document_id || rowData?.documentId || rowData?.patient_document_id || rowData?.patient_info?.document_id);
            setOpenViewModal(true);
          }}
        />
        <PrintIcon
          title={t('PrintInvoice')}
          iconSize='17'
          onClick={() =>
            navigate(`/print-invoice/${rowData?.invoice_number}`, {
              state: {
                invoiceNumber: rowData?.invoice_number,
                state: {
                  file_no: rowData?.patient?.file_no,
                },
                activeTabIndex: 1,
                activeChildTabIndex: 0,
                company: true
              }
            })
          }
        />
        {rowData?.zatca_status != 'REPORTED' && hasPermission('zatca', 'get') && isSendToZatca && (
          <IoIosSend
            className={`text-green-500  cursor-pointer `}
            title={t('Send to zatca')}
            size={20}
            onClick={async () => {
              setOpenSendToZatcaModal(true);
              setSelectedInvoice(rowData);
            }}
          />
        )}
      </div>
    );
  }

  const onPageChange = async (newPage, newRowsPerPage) => {
    setSelectedInvoices([]);
    setPayload(prevPayload => ({
      ...prevPayload,
      page: newPage,
      per_page: newRowsPerPage
    }));
  };

  const handleSendToZatca = async () => {
    if (!selectedInvoice?.insurance_invoice_number) {
      toast.error('No invoice number found.');
      return;
    }
    setZatcaLoading(true);
    const response = await fetchData('POST', '/v3/zatca/sendInsuranceInvoiceToZatca', {
      insurance_invoice_number: selectedInvoice?.insurance_invoice_number
    });
    if (response?.message) {
      setOpenSendToZatcaModal(false);
      setSelectedInvoice(null);
      toast.success(response?.message);
      fetchInitialData();
    }
    setZatcaLoading(false);
  };

  return (
    <div className=''>
      {/* {data?.data?.length > 0 && !loading && (
        <div className="mb-4 summary-container p-4 border rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
            {SummaryData(data, t)?.map((stat, index) => (
              <div
                key={index}
                className={`${stat.color} rounded-lg p-2  border border-gray-100 hover:shadow transition-all`}
              >
                <div className="flex items-center">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {stat.label}
                    </p>
                    <p
                      className={`text-xl font-bold mt-1 ${stat.color
                        .replace("bg-", "text-")
                        .replace("100", "600")}`}
                    >
                      {stat.value}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )} */}
      <div className='flex flex-col xl:flex-row  xl:justify-between mb-2 xl:mb-0'>
                <BillingFilters
                    applyFilters={applyFilters}
          payloadData={payload}
          setPayloadData={setPayload}
          refetch={refetch}
          invoiceType='insurance'
          setSearchQuery={setSearchQuery}
          fetchInitialData={fetchInitialData}
          searchQuery={searchQuery}
          resetFilters={() => {
            resetPayload();
            setSearchQuery('');
          }}
          name={'insurance_invoices'}
          exportOptionsFormat={exportCompanyInvoicesToExcelColumns}
          exportApiUrl={'/v3/invoices/exportCompanyInoviceExcel'}
        />
      </div>
      <CustomTable
        isLoading={loading}
        data={data?.data || []}
        columns={columnsData}
        rowClassName={row => (row?.is_company_cancelled ? '!bg-red-100' : '')}
      />
      {data?.data?.length > 0 && !loading && (
        <CustomPagination
          currentPage={data?.page || payload?.page || 1}
          rowsPerPage={data?.per_page || payload?.per_page || 20}
          totalRecords={data?.totalRecords}
          totalPages={data?.totalPages}
          onPageChange={onPageChange}
        />
      )}
      {/* {data?.data?.length > 0 && !loading && (
        <BillingReportSummary data={data} />
      )} */}
      <ReusableModal
        isVisible={openCancelUnpaidInvoiceModal}
        title={'Cancel Invoice'}
        HeaderIcon={FaMoneyBill}
        position='top'
        onClose={() => {
          setOpenCancelUnpaidInvoiceModal(false);
          setSelectedInvoiceData(null);
        }}
      >
        <ReturnUnpaidInvoiceForm invoiceData={selectedInvoiceData} invoiceType={'insurance'} />
      </ReusableModal>
      <ConfirmationModal
        visible={openSendToZatcaModal}
        setVisible={setOpenSendToZatcaModal}
        header={'Confirmation'}
        // message={`Are you sure you want to send invoice ${selectedInvoice?.insurance_invoice_number} to Zatca?`}
        message={`${t('Are you sure you want to send invoice')} ${selectedInvoice?.insurance_invoice_number} ${t('to Zatca?')}`}
        acceptLabel={'Send To Zatca'}
        rejectLabel={'Cancel'}
        onAccept={handleSendToZatca}
        isLoading={zatcaLoading}
      />

      <ReusableModal
        isVisible={openViewModal}
        title={t('View Invoice Details')}
        HeaderIcon={FaEye}
        position='top'
        onClose={() => {
          setOpenViewModal(false);
          setViewInvoiceNumber(null);
          setViewFileNumber(null);
          setViewPatientId(null);
          setViewDocumentId(null);
        }}
        className="w-full md:w-[95%] lg:w-[95%] xl:w-[90%]"
      >
        <ViewInvoiceModal
          invoiceNumber={viewInvoiceNumber}
          fileNumber={viewFileNumber}
          patientId={viewPatientId}
          documentId={viewDocumentId}
          isCash={false}
          onClose={() => {
            setOpenViewModal(false);
            setViewInvoiceNumber(null);
            setViewFileNumber(null);
            setViewPatientId(null);
            setViewDocumentId(null);
          }}
        />
      </ReusableModal>
    </div>
  );
}

export default InsuranceInvoice;
